import { ApiRequest } from '../baseService'

const BaseRoute = 'app/loi-question-extraction'

export interface LoiQuestionExtractionStatusDto {
  sourceAttachmentId: string
  /** None | Pending | Processing | Extracted | NeedsReview | Failed */
  status: string
  statusReason?: string
  pageFrom?: number
  pageTo?: number
  extractedFileName?: string
  hasExtractedFile: boolean
  processedTime?: string
  /**
   * True when the current user may trigger a run or delete a published copy —
   * Admin, Admin Staff and Staff, offshore included.
   */
  canRun: boolean
  /**
   * True when the current user may also open, confirm or reject a copy waiting
   * for confirmation: a runner who is onshore.
   */
  canManage: boolean
  /**
   * True when the row still says Pending/Processing but the run went silent past the
   * backend's window — a worker that died mid-run. The row shows this instead of
   * spinning forever, and a manager can start the extraction again.
   */
  isStale: boolean
  /** Which rule found the section, or 'ai' when the AI fallback did. */
  detectedBy?: string
}

/** Queues the extraction. Refused by the backend when a copy already exists. */
const Trigger = (attachmentId: string) =>
  ApiRequest.post(`${BaseRoute}/${attachmentId}/trigger`)

const GetStatus = (attachmentId: string) =>
  ApiRequest.get<LoiQuestionExtractionStatusDto>(
    `${BaseRoute}/${attachmentId}/status`
  )

/** The extracted question PDF as a blob (served offshore-readable by design). */
const DownloadExtracted = (attachmentId: string) =>
  ApiRequest.get(`${BaseRoute}/${attachmentId}/file`, { responseType: 'blob' })

/** Publishes a copy the AI found, after a manager has checked it. */
const Confirm = (attachmentId: string) =>
  ApiRequest.post(`${BaseRoute}/${attachmentId}/confirm`)

/** Deletes the extracted copy so the extraction can be run again. */
const DeleteExtracted = (attachmentId: string) =>
  ApiRequest.delete(`${BaseRoute}/${attachmentId}/extracted-copy`)

/** The QA check table as the Doc QA Result modal shows it, sent to be printed. */
export interface DocQAPrintInput {
  caseNumber?: string
  loiFileName?: string
  /** Formatted here, in the user's time zone — the server never converts dates. */
  printedAt?: string
  rows: {
    property: string
    original: string
    captured: string
    result: string
  }[]
}

/**
 * One PDF as a blob: the QA check table, then the extracted question pages when
 * this user may open them.
 */
const PrintQaResult = (attachmentId: string, input: DocQAPrintInput) =>
  ApiRequest.post(`${BaseRoute}/${attachmentId}/qa-print`, input, {
    responseType: 'blob',
  })

const API = {
  Trigger,
  GetStatus,
  DownloadExtracted,
  Confirm,
  DeleteExtracted,
  PrintQaResult,
}

export default API
